import {
  type SettingDefinitionMetadata,
  type SettingValueSource,
  globalScope,
  isSameScope,
} from '@aysar/contracts';
import { useCallback, useMemo } from 'react';

import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import {
  useClearSettingMutation,
  useSettingsDetailsQuery,
  useSettingsRegistryQuery,
  useWriteSettingMutation,
} from '@/shared/services/settings/settings.api';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import { definitionOf } from '../../utils/registry-definition/registry-definition.util';
import { BILLING_SETTING_KEYS } from '../useDirectPrices/useDirectPrices';

/** What an owned editor can say about itself. `restricted` is an ANSWER, not an error. */
export type OwnedSettingState = 'restricted' | 'loading' | 'error' | 'ready';

export interface UseOwnedSetting {
  state: OwnedSettingState;
  error: ApiError | null;
  refetch: () => void;
  /** The definition, or `null` when this server declares no such key. */
  definition: SettingDefinitionMetadata | null;
  /** The value in force at the global scope, untyped until a caller narrows it. */
  value: unknown;
  /** Which layer answered — a stored row, or the coded default. */
  source: SettingValueSource;
  /** True when an operator's stored value is deciding rather than the default. */
  isOverridden: boolean;
  /** The definition's own flag, so a confirmation is never assumed into being. */
  isDangerous: boolean;
  /** `settings.manage` — what `PUT /admin/settings/:key` actually checks. */
  mayManage: boolean;
  isSaving: boolean;
  writeGlobal: (value: unknown) => Promise<void>;
  clearGlobal: () => Promise<void>;
}

/**
 * One **global-only** settings key, read and written from the module that owns
 * its editor (P10.4's `managedBy`).
 *
 * ## Why this exists rather than a fourth copy of the same forty lines
 *
 * `usePlanMode` and `useDirectPrices` each grew this state machine — the skipped
 * queries, the synthesised "not declared" error, `isOverridden` off the source,
 * the clear-never-write-back. P26b adds two more owned keys with exactly the
 * same shape, and a second consumer is what turns a private state machine into a
 * shared hook (P23b4).
 *
 * ## Why the other two are NOT migrated onto it
 *
 * Deliberate, and not an oversight. `useDirectPrices` writes at **gender**
 * scopes and clears the row when the last override goes, and `usePlanMode` needs
 * the definition's declared `options`. Widening this hook to cover both would
 * make it the lowest common denominator of four callers and hide the one thing
 * it is for: a key with a single global value, marked dangerous, edited whole.
 *
 * It passes `BILLING_SETTING_KEYS` so the whole pricing page stays **one**
 * details request — see that constant's own docblock.
 */
export function useOwnedSetting(key: string): UseOwnedSetting {
  const { can } = useHasPermission();
  const mayRead = can('settings.read');
  const mayManage = can('settings.manage');

  const registry = useSettingsRegistryQuery(undefined, { skip: !mayRead });
  const details = useSettingsDetailsQuery({ keys: BILLING_SETTING_KEYS }, { skip: !mayRead });

  const [writeSetting, writeState] = useWriteSettingMutation();
  const [clearSetting, clearState] = useClearSettingMutation();

  const definition = useMemo(() => definitionOf(registry.data, key), [registry.data, key]);

  const effective = useMemo(
    () =>
      details.data?.items
        .find((item) => item.key === key)
        ?.effective.find((row) => isSameScope(row, globalScope())),
    [details.data, key],
  );

  const writeGlobal = useCallback(
    async (value: unknown) => {
      try {
        await writeSetting({ key, body: { ...globalScope(), value } }).unwrap();
      } catch (error) {
        throw toApiError(error);
      }
    },
    [writeSetting, key],
  );

  const clearGlobal = useCallback(async () => {
    try {
      // CLEARED, never written back as the default's own value: storing today's
      // coded default would freeze it into the database, and a later release
      // correcting it would then have no effect at all.
      await clearSetting({ key, body: globalScope() }).unwrap();
    } catch (error) {
      throw toApiError(error);
    }
  }, [clearSetting, key]);

  const loaded = !registry.isLoading && !details.isLoading;

  const requestError =
    registry.error !== undefined
      ? toApiError(registry.error)
      : details.error === undefined
        ? null
        : toApiError(details.error);

  const missingDefinition: ApiError | null =
    mayRead && loaded && requestError === null && definition === null
      ? { status: 0, code: 'setting_not_declared', message: key }
      : null;

  const error = requestError ?? missingDefinition;

  const state: OwnedSettingState = !mayRead
    ? 'restricted'
    : !loaded
      ? 'loading'
      : error !== null
        ? 'error'
        : 'ready';

  const source: SettingValueSource = effective?.source ?? 'default';

  return {
    state,
    error,
    refetch: () => {
      void registry.refetch();
      void details.refetch();
    },
    definition,
    value: effective?.value,
    source,
    isOverridden: source !== 'default',
    isDangerous: definition?.dangerous ?? false,
    mayManage,
    isSaving: writeState.isLoading || clearState.isLoading,
    writeGlobal,
    clearGlobal,
  };
}
